import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RECAPTCHA_SITE_KEY } from '../../constants/recaptcha.config';

let nextId = 0;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      render: (
        container: HTMLElement | string,
        parameters: {
          sitekey: string;
          theme?: 'light' | 'dark';
          size?: 'normal' | 'compact';
          tabindex?: number;
          callback?: (response: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => number;
      reset: (opt_widget_id?: number) => void;
      getResponse: (opt_widget_id?: number) => string;
    };
    [key: string]: any;
  }
}

@Component({
  selector: 'lib-recaptcha',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      #recaptchaContainer
      class="g-recaptcha"
      [attr.data-sitekey]="siteKey()"
      [attr.data-callback]="callbackName"
      [attr.data-expired-callback]="expiredCallbackName"
      [attr.data-error-callback]="errorCallbackName"
    ></div>
  `,
  styles: [
    `
      :host {
        display: flex;
        justify-content: center;
        width: 100%;
        margin: 0.75rem 0;
      }
      .g-recaptcha {
        min-height: 78px;
        display: flex;
        justify-content: center;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecaptchaComponent implements AfterViewInit, OnDestroy {
  private ngZone = inject(NgZone);

  readonly siteKey = input<string>(RECAPTCHA_SITE_KEY);

  readonly tokenChange = output<string>();

  @ViewChild('recaptchaContainer', { static: false })
  recaptchaContainer!: ElementRef<HTMLElement>;

  private instanceId = ++nextId;
  readonly callbackName = `__recaptcha_success_${this.instanceId}`;
  readonly expiredCallbackName = `__recaptcha_expired_${this.instanceId}`;
  readonly errorCallbackName = `__recaptcha_error_${this.instanceId}`;

  private widgetId: number | null = null;
  private isDestroyed = false;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private tokenWatcherTimer: ReturnType<typeof setInterval> | null = null;
  private lastReportedToken = '';

  constructor() {
    this.registerGlobalCallbacks();
  }

  ngAfterViewInit(): void {
    this.ensureScriptAndRender();
    this.startTokenWatcher();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.tokenWatcherTimer) {
      clearInterval(this.tokenWatcherTimer);
      this.tokenWatcherTimer = null;
    }
    this.unregisterGlobalCallbacks();
  }

  getResponse(): string {
    if (typeof window === 'undefined') return '';
    const grecaptcha = window.grecaptcha;
    if (this.widgetId !== null && typeof grecaptcha?.getResponse === 'function') {
      return grecaptcha.getResponse(this.widgetId) || '';
    }
    return typeof grecaptcha?.getResponse === 'function'
      ? grecaptcha.getResponse() || ''
      : '';
  }

  reset(): void {
    if (typeof window === 'undefined') return;
    const grecaptcha = window.grecaptcha;
    if (this.widgetId !== null && typeof grecaptcha?.reset === 'function') {
      try {
        grecaptcha.reset(this.widgetId);
      } catch (err) {
        console.warn('grecaptcha.reset error', err);
      }
    } else if (typeof grecaptcha?.reset === 'function') {
      try {
        grecaptcha.reset();
      } catch (err) {
        console.warn('grecaptcha.reset error', err);
      }
    }
    this.lastReportedToken = '';
    this.tokenChange.emit('');
  }

  private registerGlobalCallbacks(): void {
    if (typeof window === 'undefined') return;

    window[this.callbackName] = (token: string) => {
      this.ngZone.run(() => {
        this.lastReportedToken = token || '';
        this.tokenChange.emit(this.lastReportedToken);
      });
    };

    window[this.expiredCallbackName] = () => {
      this.ngZone.run(() => {
        this.lastReportedToken = '';
        this.tokenChange.emit('');
      });
    };

    window[this.errorCallbackName] = () => {
      this.ngZone.run(() => {
        this.lastReportedToken = '';
        this.tokenChange.emit('');
      });
    };
  }

  private unregisterGlobalCallbacks(): void {
    if (typeof window === 'undefined') return;
    delete window[this.callbackName];
    delete window[this.expiredCallbackName];
    delete window[this.errorCallbackName];
  }

  private startTokenWatcher(): void {
    if (typeof window === 'undefined') return;
    this.tokenWatcherTimer = setInterval(() => {
      if (this.isDestroyed) return;
      const currentToken = this.getResponse();
      if (currentToken !== this.lastReportedToken) {
        this.lastReportedToken = currentToken;
        this.ngZone.run(() => {
          this.tokenChange.emit(currentToken);
        });
      }
    }, 250);
  }

  private ensureScriptAndRender(): void {
    if (typeof window === 'undefined') return;

    if (!document.querySelector('script[src*="recaptcha/api.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const checkAndRender = () => {
      if (this.isDestroyed) return;
      const grecaptcha = window.grecaptcha;
      if (grecaptcha && typeof grecaptcha.render === 'function') {
        grecaptcha.ready(() => {
          this.ngZone.runOutsideAngular(() => {
            this.renderWidget();
          });
        });
      } else {
        this.pollTimer = setTimeout(checkAndRender, 100);
      }
    };

    checkAndRender();
  }

  private renderWidget(): void {
    if (this.isDestroyed || !this.recaptchaContainer?.nativeElement) return;
    const container = this.recaptchaContainer.nativeElement;

    if (container.childNodes.length > 0) return;

    const grecaptcha = window.grecaptcha;
    if (!grecaptcha || typeof grecaptcha.render !== 'function') return;

    try {
      this.widgetId = grecaptcha.render(container, {
        sitekey: this.siteKey(),
        callback: (token: string) => {
          this.ngZone.run(() => {
            this.lastReportedToken = token || '';
            this.tokenChange.emit(this.lastReportedToken);
          });
        },
        'expired-callback': () => {
          this.ngZone.run(() => {
            this.lastReportedToken = '';
            this.tokenChange.emit('');
          });
        },
        'error-callback': () => {
          this.ngZone.run(() => {
            this.lastReportedToken = '';
            this.tokenChange.emit('');
          });
        },
      });
    } catch (err) {
      console.warn('Error rendering reCAPTCHA widget:', err);
    }
  }
}
